function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { AbstractApplication, log } from '@netless/forge-room';
import * as Y from 'yjs';
import paper from 'paper';
import { RenderableModel } from './model/RenderableModel';
import { CurveTool } from './tool/CurveTool';
import { RectangleTool } from './tool/RectangleTool';
import { TOOLBAR_KEYS, ToolbarModel } from './model/ToolbarModel';
import { SelectorTool } from './tool/SelectorTool';
import { SelectElementsModel } from './model/SelectElementsModel';
import { Editor } from './edit/Editor';
import { LineTool } from './tool/LineTool';
import { TextTool } from './tool/TextTool';
import { Camera } from './Camera';
import { EllipseTool } from './tool/EllipseTool';
import { TriangleTool } from './tool/TriangleTool';
import { elementsUndoOrigin } from './utils/constant';
import { Whiteboard } from './Whiteboard';
import { EraserTool } from './tool/EraserTool';
import { TrashedElementsModel } from './model/TrashedElementsModel';
import { LaserPointerTool } from './tool/LaserPointerTool';
import { PageModel } from './model/PageModel';
import { waitUntil } from './utils/async';
import { IndexedNavigation } from './IndexedNavigation';
import { WhiteboardKeys } from './WhiteboardKeys';
import { WhiteboardPermissionFlag, WhiteboardPermissions } from './WhitePermissions';
import { ShadowEmitter } from './utils/ShadowEmitter';
import { requestAnimationFrameHook } from './utils/paperjs';
import { StraightLineTool } from './tool/StraightLineTool';
import { GrabTool } from './tool/GrabTool';
import { PointerTool } from './tool/PointerTool';
import { SequenceExecutor } from './SequenceExecutor';
import { removeObserver } from '@netless/forge-room';
import { LiveCursor } from './LiveCursor';

/**
 * 白板启动配置, 同一个房间中不同客户端应使用一致的启动配置项, 推荐直接硬编码在代码中
 */

export const WHITEBOARD_APP_NAME = 'whiteboard';
class AsyncMap {
  constructor() {
    _defineProperty(this, "map", void 0);
    this.map = new Map();
  }
  get(key) {
    return Promise.resolve(this.map.get(key));
  }
  set(key, value) {
    this.map.set(key, value);
  }
}
if (!window.__forge_gl_wb_status__) {
  window.__forge_gl_wb_status__ = new AsyncMap();
}
export class WhiteboardApplication extends AbstractApplication {
  get undoManager() {
    const page = this.pageModel.getCurrentPage(this.userId);
    if (page) {
      return this.undoManagers.get(page) ?? null;
    }
    return null;
  }
  get viewportWidth() {
    const vw = this.getMap('attrs').get('viewportWidth');
    if (vw) {
      return vw;
    }
    // @ts-ignore 兼容
    return this.option.width;
  }
  get viewportHeight() {
    const vh = this.getMap('attrs').get('viewportHeight');
    if (vh) {
      return vh;
    }
    // @ts-ignore 兼容
    return this.option.height;
  }
  constructor() {
    var _this;
    super();
    _this = this;
    _defineProperty(this, "name", WHITEBOARD_APP_NAME);
    _defineProperty(this, "emitter", void 0);
    _defineProperty(this, "toolbarModel", void 0);
    _defineProperty(this, "selectElementsModel", void 0);
    _defineProperty(this, "trashedElementsModel", void 0);
    _defineProperty(this, "pageModel", void 0);
    _defineProperty(this, "layers", new Map());
    _defineProperty(this, "undoManagers", new Map());
    _defineProperty(this, "imageSets", document.createElement('div'));
    _defineProperty(this, "rootElement", document.createElement('div'));
    _defineProperty(this, "paperScope", new paper.PaperScope());
    _defineProperty(this, "canvasElement", document.createElement('canvas'));
    _defineProperty(this, "shadowScope", new paper.PaperScope());
    _defineProperty(this, "snapshotCanvasElement", document.createElement('canvas'));
    _defineProperty(this, "snapshotScope", new paper.PaperScope());
    _defineProperty(this, "shadowCanvasElement", document.createElement('canvas'));
    _defineProperty(this, "shadowEmitter", void 0);
    _defineProperty(this, "tools", void 0);
    _defineProperty(this, "editors", new Map());
    _defineProperty(this, "camera", void 0);
    _defineProperty(this, "resizeObserver", void 0);
    _defineProperty(this, "option", void 0);
    _defineProperty(this, "permissions", void 0);
    _defineProperty(this, "inputType", 'any');
    _defineProperty(this, "isPenEvent", false);
    _defineProperty(this, "activePenPointerId", null);
    _defineProperty(this, "hasPenInput", null);
    _defineProperty(this, "disableViewModelUpdate", false);
    _defineProperty(this, "internalResizeObserver", true);
    _defineProperty(this, "sequenceExecutor", new SequenceExecutor());
    _defineProperty(this, "linkWhiteboardId", null);
    _defineProperty(this, "liveCursor", void 0);
    _defineProperty(this, "delayTranslateOut", -1);
    _defineProperty(this, "addWhiteboardStatus", evt => {
      if (evt.detail.whiteboardAppId && evt.detail.status) {
        if (this.linkWhiteboardId === evt.detail.whiteboardAppId) {
          this.toolbarModel.currentTool = evt.detail.status.currentTool;
          this.toolbarModel.strokeColor = evt.detail.status.strokeColor;
          this.toolbarModel.fillColor = evt.detail.status.fillColor;
          this.toolbarModel.fontSize = evt.detail.status.fontSize;
          this.toolbarModel.fontFamily = evt.detail.status.fontFamily;
          this.toolbarModel.strokeWidth = evt.detail.status.strokeWidth;
          this.toolbarModel.dashArray = evt.detail.status.dashArray;
          this.permissions.removePermission(WhiteboardPermissionFlag.all);
          this.permissions.addPermission(evt.detail.status.permission);
        }
      }
    });
    _defineProperty(this, "enableToolEvent", () => {
      return !(this.inputType === 'pen' && !this.isPenEvent);
    });
    _defineProperty(this, "handleWritableChanged", (userId, writable) => {
      // writeable 状态变更时，重新建立白板链接, 因为只读用户在链接白板时因为没有权限状态同步会失败
      if (userId === this.userId && writable && this.linkWhiteboardId) {
        this.linkToWhiteboard(this.linkWhiteboardId);
      }
    });
    _defineProperty(this, "handleViewportUpdate", evt => {
      if (evt.keysChanged.has('viewportWidth') || evt.keysChanged.has('viewportHeight')) {
        const rect = this.rootElement.getBoundingClientRect();
        this.adjustByOutFrame(rect.width, rect.height);
      }
    });
    _defineProperty(this, "handleElementTranslateOut", (ids, container) => {
      const shadowLayer = this.shadowScope.project.activeLayer;
      let parent = null;
      if (container === 'layer') {
        parent = shadowLayer;
      } else {
        parent = shadowLayer.children.find(child => child.data.uuid === container) ?? null;
      }
      if (parent) {
        ids.forEach(id => {
          const target = parent.children.find(child => child.data.uuid === id);
          if (target) {
            target.remove();
            this.insertElementToParent(target, this.paperScope.project.activeLayer);
          } else {
            const pageId = this.pageModel.getCurrentPage(this.userId);
            if (pageId) {
              const page = this.layers.get(pageId);
              if (page) {
                const model = page.elementModels.get(id);
                if (model && model.item) {
                  this.insertElementToParent(model.item, this.paperScope.project.activeLayer);
                }
              }
            }
          }
        });
      }
    });
    _defineProperty(this, "handlePageSwitch", pageId => {
      this.sequenceExecutor.addTask(async () => {
        for (const editor of this.editors.values()) {
          editor.hidden();
        }
        this.selectElementsModel.clearSelectElementForSelf();
        await waitUntil(() => this.layers.has(pageId), 1000);
        if (this.layers.has(pageId)) {
          for (const entry of this.layers.entries()) {
            entry[1].off('elementInsert', this.handleElementInsert);
            entry[1].off('elementRemove', this.handleElementRemove);
            entry[1].off('elementClear', this.handleElementClear);
          }
          const renderableModel = this.layers.get(pageId);
          renderableModel.on('elementInsert', this.handleElementInsert);
          renderableModel.on('elementRemove', this.handleElementRemove);
          renderableModel.on('elementClear', this.handleElementClear);
          renderableModel.flushRenderables();
          this.paperScope.project.activeLayer.data.pageId = pageId;
          const others = this.userManager.userIdList().filter(v => v !== this.userId);
          for (const other of others) {
            if (this.userMap(other).get(WhiteboardKeys.currentPage) === pageId) {
              const elementIds = Array.from(this.userMap(other).get(WhiteboardKeys.selectElements).keys()).filter(v => v !== 'inner-map-id');
              if (elementIds.length > 0) {
                this.handleElementsSelect(other, elementIds);
              }
            }
          }
        } else {
          console.warn(`[@netless/forge-whiteboard] page ${pageId} not found`);
          log(`[@netless/forge-whiteboard] page ${pageId} not found`, {}, 'warn');
        }
        await waitUntil(() => this.undoManagers.has(pageId), 1000);
        if (this.undoManagers.has(pageId)) {
          for (const entry of this.undoManagers.entries()) {
            entry[1].off('stack-item-added', this.handleStackItemAdded);
            entry[1].off('stack-item-popped', this.handleStackItemPopped);
          }
          this.undoManagers.get(pageId).on('stack-item-added', this.handleStackItemAdded);
          this.undoManagers.get(pageId).on('stack-item-popped', this.handleStackItemPopped);
        } else {
          console.warn(`[@netless/forge-whiteboard] undo manager for page ${pageId} not found`);
          log(`[@netless/forge-whiteboard] undo manager for page ${pageId} not found`, {}, 'warn');
        }
        this.emitter.emit('redoStackLength', this.undoManager?.redoStack.length ?? 0);
        this.emitter.emit('undoStackLength', this.undoManager?.undoStack.length ?? 0);
      });
    });
    _defineProperty(this, "hasPermission", flag => {
      return this.permissions.hasPermission(flag, this.userId);
    });
    _defineProperty(this, "isPerformanceMode", () => {
      return this.option.performance ?? false;
    });
    _defineProperty(this, "handleStackItemAdded", () => {
      this.selectElementsModel.clearSelectElementForSelf();
      this.emitter.emit('redoStackLength', this.undoManager?.redoStack.length ?? 0);
      this.emitter.emit('undoStackLength', this.undoManager?.undoStack.length ?? 0);
    });
    _defineProperty(this, "handleStackItemPopped", () => {
      this.emitter.emit('redoStackLength', this.undoManager?.redoStack.length ?? 0);
      this.emitter.emit('undoStackLength', this.undoManager?.undoStack.length ?? 0);
    });
    _defineProperty(this, "handleLayersChange", ids => {
      for (let i = 0, len = ids.length; i < len; i++) {
        const id = ids[i];
        if (!this.layers.has(id)) {
          const elementsMap = this.getMap(`layer/${id}/elements`);
          const renderableModel = new RenderableModel({
            layerId: id,
            shadowEmitter: this.shadowEmitter,
            elements: elementsMap,
            scope: this.paperScope,
            toolbarModel: this.toolbarModel,
            userManager: this.userManager,
            imageSets: this.imageSets,
            liveCursor: this.liveCursor,
            hasPermission: this.hasPermission,
            isPerformanceMode: this.isPerformanceMode
          });
          this.layers.set(id, renderableModel);
        }
        if (!this.undoManagers.has(id)) {
          const elementsMap = this.getMap(`layer/${id}/elements`);
          const undoManager = new Y.UndoManager(elementsMap, {
            trackedOrigins: new Set([elementsUndoOrigin])
          });
          this.undoManagers.set(id, undoManager);
        }
      }

      // layer 被删除
      for (const entry of this.layers.entries()) {
        if (ids.indexOf(entry[0]) < 0) {
          const renderableModel = this.layers.get(entry[0]);
          this.layers.delete(entry[0]);
          if (renderableModel) {
            renderableModel.dispose(true);
          }
          const cameraMode = this.userMap(this.userId).get(WhiteboardKeys.cameraMode);
          const currentPage = this.userMap(this.userId).get(WhiteboardKeys.currentPage);
          if (cameraMode === 'free' && entry[0] === currentPage) {
            this.userMap(this.userId).set(WhiteboardKeys.cameraMode, 'main');
            this.userMap(this.userId).set(WhiteboardKeys.currentPage, this.getMap('attrs').get(WhiteboardKeys.currentPage));
          }
        }
      }
    });
    _defineProperty(this, "getCurrentRendererModel", () => {
      const layerId = this.userMap(this.userId).get(WhiteboardKeys.currentPage);
      if (this.layers.has(layerId)) {
        return Promise.resolve(this.layers.get(layerId));
      } else {
        return waitUntil(() => this.layers.has(layerId), 1000).then(() => {
          if (this.layers.has(layerId)) {
            return this.layers.get(layerId);
          } else {
            this.emitter.emit('error', 300002, `target page: ${layerId} not found`);
            return null;
          }
        });
      }
    });
    _defineProperty(this, "handleElementClear", () => {
      this.shadowScope.project.activeLayer.removeChildren();
      this.paperScope.project.activeLayer.removeChildren();
    });
    _defineProperty(this, "handleElementInsert", elements => {
      for (const element of elements) {
        element.createPaperElement();
        const scope = element.shadow === '' ? this.paperScope : this.shadowScope;
        const children = scope.project.activeLayer.children;
        const oldPaperElement = children.find(v => v.data.uuid === element.uuid);
        if (oldPaperElement) {
          oldPaperElement.remove();
        }
        this.insertElementToParent(element.item, scope.project.activeLayer);
      }
    });
    _defineProperty(this, "handleElementRemove", (elementId, layerId) => {
      const layerModel = this.layers.get(layerId);
      if (!layerModel) {
        return;
      }
      const target = layerModel.elementModels.get(elementId);
      if (target && target.item) {
        target.item.remove();
      }
      layerModel.elementModels.delete(elementId);
    });
    _defineProperty(this, "handleRemoveTrashedElementForSelf", (userId, elementIds) => {
      this.appDoc.transact(() => {
        const selfLayerId = this.userMap(this.userId).get(WhiteboardKeys.currentPage);
        for (const elementId of elementIds) {
          this.layers.get(selfLayerId)?.removeElementItem(elementId);
        }
      }, elementsUndoOrigin);
    });
    _defineProperty(this, "handleElementsTrash", (userId, elements) => {
      const targetLayerId = this.userMap(userId).get(WhiteboardKeys.currentPage);
      const selfLayerId = this.userMap(this.userId).get(WhiteboardKeys.currentPage);
      if (targetLayerId !== selfLayerId || !this.layers.has(targetLayerId)) {
        return;
      }
      elements.forEach(id => {
        const paperItem = this.layers.get(targetLayerId).elementModels.get(id);
        if (paperItem && paperItem.item) {
          paperItem.item.opacity = 0.3;
        }
      });
    });
    _defineProperty(this, "handleElementsSelect", (userId, elements) => {
      if (!this.editors.has(userId)) {
        const ctrl = this.userManager.getUser(userId);
        const editor = new Editor(this.paperScope, this.shadowScope, this.shadowEmitter, this.canvasElement, this.camera, {
          viewerId: this.userId,
          ctrlId: userId,
          ctrlNickName: ctrl?.nickName ?? '',
          ctrlUserMap: this.userMap(userId)
        });
        editor.on('clear', () => {
          this.selectElementsModel.clearSelectElementForSelf();
        });
        this.editors.set(userId, editor);
        this.rootElement.appendChild(editor.rootView);
      }
      const editor = this.editors.get(userId);
      if (elements.length === 0) {
        editor.hidden();
        this.emitter.emit('elementDeselected', userId);
        return;
      }
      const targetLayerId = this.userMap(userId).get(WhiteboardKeys.currentPage);
      const selfLayerId = this.userMap(this.userId).get(WhiteboardKeys.currentPage);
      if (targetLayerId !== selfLayerId || !this.layers.has(targetLayerId)) {
        return;
      }
      const elementModels = elements.map(id => {
        return this.layers.get(targetLayerId).elementModels.get(id);
      }).filter(model => !!model && !model.isBackground);
      if (elementModels.length === 0) {
        editor.hidden();
        this.emitter.emit('elementDeselected', userId);
        return;
      }
      editor.show();
      editor.setTargets(elementModels);
      if (elementModels.length === 1) {
        const model = elementModels[0];
        if (model.item) {
          const topLeft = this.paperScope.project.view.projectToView(model.item.bounds.topLeft);
          const bottomRight = this.paperScope.project.view.projectToView(model.item.bounds.bottomRight);
          this.emitter.emit('elementSelected', userId, targetLayerId, model.uuid, model.getStyleKeys(), [topLeft.x, topLeft.y, bottomRight.x - topLeft.x, bottomRight.y - topLeft.y]);
        }
      }
      if (elementModels.length > 1) {
        this.emitter.emit('elementDeselected', userId);
      }
    });
    _defineProperty(this, "handleSyncedWhiteboardStatusChange", evt => {
      if ([TOOLBAR_KEYS.tool, TOOLBAR_KEYS.strokeColor, TOOLBAR_KEYS.fillColor, TOOLBAR_KEYS.fontSize, TOOLBAR_KEYS.fontFamily, TOOLBAR_KEYS.strokeWidth, TOOLBAR_KEYS.dashArray, 'permission'].some(key => evt.keysChanged.has(key))) {
        const nextState = {
          currentTool: evt.target.get(TOOLBAR_KEYS.tool),
          strokeColor: evt.target.get(TOOLBAR_KEYS.strokeColor),
          fillColor: evt.target.get(TOOLBAR_KEYS.fillColor),
          fontSize: evt.target.get(TOOLBAR_KEYS.fontSize),
          fontFamily: evt.target.get(TOOLBAR_KEYS.fontFamily),
          strokeWidth: evt.target.get(TOOLBAR_KEYS.strokeWidth),
          dashArray: evt.target.get(TOOLBAR_KEYS.dashArray),
          permission: evt.target.get('permission')
        };
        window.__forge_gl_wb_status__.set(this.appId, nextState);
        window.dispatchEvent(new CustomEvent('forge-whiteboard-synced-status', {
          detail: {
            whiteboardAppId: this.appId,
            status: nextState,
            userId: this.userId
          }
        }));
      }
    });
    _defineProperty(this, "adjustByOutFrame", (frameWidth, frameHeight) => {
      if (!this.paperScope.project.view || !this.shadowScope.project.view) {
        return;
      }
      const viewportWidth = this.viewportWidth;
      const viewportHeight = this.viewportHeight;
      if (viewportWidth > 0 && viewportHeight > 0) {
        const minWidth = Math.max(frameWidth, 10);
        const minHeight = Math.max(frameHeight, 10);
        let width = minWidth;
        let height = width * viewportHeight / viewportWidth;
        if (height > minHeight) {
          height = minHeight;
          width = height * viewportWidth / viewportHeight;
        }
        this.camera.updateInherentScale(width / viewportWidth);
        this.paperScope.project.view.viewSize = new this.paperScope.Size(width, height);
        this.shadowScope.project.view.viewSize = new this.paperScope.Size(width, height);
        this.camera.triggerZoom();
      }
    });
    requestAnimationFrameHook(this.paperScope);
    requestAnimationFrameHook(this.shadowScope);
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;
    this.rootElement.setAttribute('data-forge-app', 'whiteboard');
    this.imageSets.style.display = 'none';
    this.imageSets.setAttribute('data-image-sets', '');
    this.rootElement.appendChild(this.imageSets);
    document.body.addEventListener('pointerdown', evt => {
      if (evt.pointerType === 'pen') {
        this.isPenEvent = true;
        this.activePenPointerId = evt.pointerId;
        if (this.hasPenInput === null) {
          this.hasPenInput = true;
          this.inputType = 'pen';
          this.emitter.emit('inputTypeChange', this.inputType);
        }
      } else {
        // 只有在没有活跃 Pencil 笔画时，才把 isPenEvent 置为 false
        if (this.activePenPointerId === null) {
          this.isPenEvent = false;
        }
      }
    }, {
      capture: true
    });
    document.body.addEventListener('pointerup', evt => {
      if (evt.pointerId === this.activePenPointerId) {
        this.activePenPointerId = null;
        this.isPenEvent = false;
      }
    }, {
      capture: true
    });
    document.body.addEventListener('pointercancel', evt => {
      if (evt.pointerId === this.activePenPointerId) {
        this.activePenPointerId = null;
        this.isPenEvent = false;
      }
    }, {
      capture: true
    });
    this.emitter = new Whiteboard(this.rootElement);
    this.emitter.getElementAttribute = (layerId, elementId, attribute) => {
      const layerModel = this.layers.get(layerId);
      if (layerModel) {
        const element = layerModel.elementModels.get(elementId);
        if (element) {
          // @ts-ignore
          return element[attribute];
        }
      }
      return undefined;
    };
    this.emitter.setElementAttribute = (layerId, elementId, attribute, value) => {
      const layerModel = this.layers.get(layerId);
      if (layerModel) {
        const element = layerModel.elementModels.get(elementId);
        if (element) {
          // @ts-ignore
          element[attribute] = value;
        }
      }
    };
    this.emitter.getCurrentTool = () => {
      return this.toolbarModel.currentTool;
    };
    this.emitter.setCurrentTool = tool => {
      this.toolbarModel.currentTool = tool;
    };
    this.emitter.setThemeColor = color => {
      this.userMap(this.userId).set(WhiteboardKeys.themeColor, color);
    };
    this.emitter.gotoPage = id => {
      this.pageModel.gotoPage(id);
    };
    this.emitter.setFreeModelUserPage = (id, userId) => {
      this.pageModel.setUserPage(id, userId);
    };
    this.emitter.addPage = id => {
      return this.pageModel.addPage(id);
    };
    this.emitter.deletePage = id => {
      return this.pageModel.deletePage(id);
    };
    this.emitter.pageList = () => {
      return this.pageModel.pageList();
    };
    this.emitter.currentPageId = userId => {
      const targetId = userId ?? this.userId;
      return this.userMap(targetId).get(WhiteboardKeys.currentPage);
    };
    this.emitter.setViewModeToFree = userId => {
      if (that.disableViewModelUpdate) {
        console.warn('Operation failed. Perspective mode switching is disabled in the current environment.');
        log('Operation failed. Perspective mode switching is disabled in the current environment.', {}, 'warn');
        return;
      }
      const targetId = userId ? this.hasPermission(WhiteboardPermissionFlag.setOthersView) ? userId : null : this.userId;
      if (targetId) {
        this.userMap(targetId).set(WhiteboardKeys.cameraMode, 'free');
      }
    };
    this.emitter.setViewModeToFlow = (flowId, userId) => {
      if (that.disableViewModelUpdate) {
        console.warn('Operation failed. Perspective mode switching is disabled in the current environment.');
        log('Operation failed. Perspective mode switching is disabled in the current environment.', {}, 'warn');
        return;
      }
      const targetId = userId ? this.hasPermission(WhiteboardPermissionFlag.setOthersView) ? userId : null : this.userId;
      if (targetId && this.userManager.getUser(flowId)) {
        this.userMap(targetId).set(WhiteboardKeys.cameraMode, flowId);
        const flowPage = this.userMap(flowId).get(WhiteboardKeys.currentPage);
        this.userMap(targetId).set(WhiteboardKeys.currentPage, flowPage);
        this.handlePageSwitch(flowPage);
        this.camera.resetViewMatrixToFlow(flowId);
      }
    };
    this.emitter.setPerformanceMode = enabled => {
      this.option.performance = enabled;
    };
    this.emitter.isPerformanceMode = () => {
      return this.isPerformanceMode();
    };
    this.emitter.setViewModeToMain = userId => {
      if (that.disableViewModelUpdate) {
        console.warn('Operation failed. Perspective mode switching is disabled in the current environment.');
        log('Operation failed. Perspective mode switching is disabled in the current environment.', {}, 'warn');
        return;
      }
      const targetId = userId ? this.hasPermission(WhiteboardPermissionFlag.setOthersView) ? userId : null : this.userId;
      if (targetId) {
        this.userMap(targetId).set(WhiteboardKeys.cameraMode, 'main');
        const mainPage = this.getMap('attrs').get(WhiteboardKeys.currentPage);
        this.userMap(targetId).set(WhiteboardKeys.currentPage, mainPage);
        this.handlePageSwitch(mainPage);
        this.camera.resetViewMatrixToMain();
      }
    };
    this.emitter.insertImage = (src, options) => {
      if (!/https/.test(src)) {
        log('[@netless/forge-whiteboard] invalid image url, src needs to be in the HTTPS protocol.', {
          src
        }, 'warn');
        return;
      }
      const normalizedOptions = typeof options === 'string' ? {
        pageId: options
      } : options ?? {};
      let targetPageId = normalizedOptions.pageId;
      if (!targetPageId) {
        targetPageId = this.pageModel.getCurrentPage(this.userManager.selfId);
      }
      if (!targetPageId) {
        log('[@netless/forge-whiteboard] page not found', {}, 'warn');
        return;
      }
      this.layers.get(targetPageId)?.createImage(src, normalizedOptions.fit ?? 'legacy', normalizedOptions.role ?? 'normal');
    };
    this.emitter.removeElement = (pageId, elementId) => {
      if (!this.layers.has(pageId)) {
        log('[@netless/forge-whiteboard] page not found', {}, 'warn');
        return;
      }
      if (this.layers.get(pageId)?.isBackgroundElement(elementId)) {
        return;
      }
      this.layers.get(pageId)?.removeElementItem(elementId);
    };
    this.emitter.getViewModel = userId => {
      const targetId = userId ? userId : this.userId;
      return this.userMap(targetId).get(WhiteboardKeys.cameraMode);
    };
    this.emitter.setCanvasBackgroundColor = color => {
      this.canvasElement.style.background = color;
    };
    this.emitter.clonePage = (sourceId, targetId) => {
      if (this.pageModel.pageList().indexOf(targetId) >= 0) {
        return;
      }
      this.pageModel.addPage(targetId);
      const source = this.getMap(`layer/${sourceId}/elements`);
      const target = this.getMap(`layer/${targetId}/elements`);
      if (!this.layers.has(targetId)) {
        this.layers.set(targetId, new RenderableModel({
          layerId: targetId,
          shadowEmitter: this.shadowEmitter,
          elements: target,
          scope: this.paperScope,
          toolbarModel: this.toolbarModel,
          userManager: this.userManager,
          imageSets: this.imageSets,
          liveCursor: this.liveCursor,
          hasPermission: this.hasPermission,
          isPerformanceMode: this.isPerformanceMode
        }));
      }
      if (!this.undoManagers.has(targetId)) {
        const undoManager = new Y.UndoManager(target, {
          trackedOrigins: new Set([elementsUndoOrigin])
        });
        this.undoManagers.set(targetId, undoManager);
      }
      if (source) {
        for (const key of source.keys()) {
          target.set(key, source.get(key).clone());
        }
      }
    };
    this.emitter.clearPage = () => {
      if (!this.writableManager().isWritable()) {
        return;
      }
      this.getCurrentRendererModel().then(model => {
        if (model) {
          if (model.elements.doc) {
            model.elements.doc.transact(() => {
              for (const key of Array.from(model.elements.keys())) {
                if (!model.isBackgroundElement(key)) {
                  model.elements.delete(key);
                }
              }
            }, elementsUndoOrigin);
          } else {
            model.elementModels.clear();
          }
        }
      });
    };
    this.emitter.undo = () => {
      if (!this.writableManager().isWritable()) {
        return;
      }
      this.undoManager?.undo();
    };
    this.emitter.redo = () => {
      if (!this.writableManager().isWritable()) {
        return;
      }
      this.undoManager?.redo();
    };
    this.emitter.rasterizeViewport = (scale, page) => {
      return this.rasterize(1, 1, scale, 'viewport', page);
    };
    this.emitter.rasterizeElementsBounds = (outputMaxWidth, outputMaxHeight, page) => {
      return this.rasterize(outputMaxWidth, outputMaxHeight, 1, 'bounds', page);
    };
    this.emitter.rasterizeMaxBounds = (scale, page) => {
      return this.rasterize(1, 1, scale, 'maxScale', page);
    };
    this.emitter.setInputType = type => {
      this.inputType = type;
      this.emitter.emit('inputTypeChange', this.inputType);
    };
    this.emitter.translateCamera = (offsetX, offsetY) => {
      this.camera.translate(offsetX, offsetY);
    };
    this.emitter.scaleCamera = function (scale) {
      let origin = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'center';
      _this.camera.scale(scale, origin);
    };
    this.emitter.resetCamera = () => {
      this.camera.reset();
    };
    this.emitter.showLiveCursor = value => {
      this.liveCursor.showLiveCursor = value;
    };
    this.emitter.updateViewport = (width, height) => {
      log(`call updateViewport with width: ${width}, height: ${height}`);
      this.updateOptionSize(width, height);
    };
    // @ts-ignore
    this.emitter.__setMainCanvasVisible = visible => {
      log(`call __setMainCanvasVisible with visible: ${visible}`);
      this.canvasElement.style.opacity = visible ? '1' : '0';
    };
    this.emitter.on('error', (errorCode, errorMessage) => {
      log('WhiteboardApplicationError', {
        errorCode,
        errorMessage
      });
    });
    Object.defineProperty(this.emitter, '__delayTranslateOut', {
      get() {
        return that.delayTranslateOut;
      },
      set(value) {
        log(`call __delayTranslateOut with value: ${value}`);
        that.delayTranslateOut = value;
      }
    });
    Object.defineProperty(this.emitter, 'tool', {
      get() {
        return that.toolbarModel.currentTool;
      },
      set(value) {
        that.toolbarModel.currentTool = value;
      }
    });
    Object.defineProperty(this.emitter, 'fontSize', {
      get() {
        return that.toolbarModel.fontSize;
      },
      set(value) {
        that.toolbarModel.fontSize = value;
      }
    });
    Object.defineProperty(this.emitter, 'fontFamily', {
      get() {
        return that.toolbarModel.fontFamily;
      },
      set(value) {
        that.toolbarModel.fontFamily = value;
      }
    });
    Object.defineProperty(this.emitter, 'strokeColor', {
      get() {
        return that.toolbarModel.strokeColor;
      },
      set(value) {
        that.toolbarModel.strokeColor = value;
      }
    });
    Object.defineProperty(this.emitter, 'fillColor', {
      get() {
        return that.toolbarModel.fillColor;
      },
      set(value) {
        that.toolbarModel.fillColor = value;
      }
    });
    Object.defineProperty(this.emitter, 'strokeWidth', {
      get() {
        return that.toolbarModel.strokeWidth;
      },
      set(value) {
        that.toolbarModel.strokeWidth = value;
      }
    });
    Object.defineProperty(this.emitter, 'dashArray', {
      get() {
        return that.toolbarModel.dashArray;
      },
      set(v) {
        that.toolbarModel.dashArray = v;
      }
    });
    Object.defineProperty(this.emitter, 'enableCameraByMouse', {
      get() {
        return that.camera.enableByMouse;
      },
      set(value) {
        that.camera.enableByMouse = value;
      }
    });
    Object.defineProperty(this.emitter, 'enableCameraByTouch', {
      get() {
        return that.camera.enableByTouch;
      },
      set(value) {
        that.camera.enableByTouch = value;
      }
    });
    Object.defineProperty(this.emitter, 'cameraBoundaryColor', {
      get() {
        return that.camera.boundaryColor;
      },
      set(value) {
        that.camera.boundaryColor = value;
      }
    });
    Object.defineProperty(this.emitter, 'enableCameraBoundaryHighlight', {
      get() {
        return that.camera.enableBoundaryHighlight;
      },
      set(value) {
        that.camera.enableBoundaryHighlight = value;
      }
    });

    // @ts-ignore
    window.addEventListener('forge-whiteboard-synced-status', this.addWhiteboardStatus);
  }
  addPage(id) {
    let focus = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
    return this.pageModel.addPage(id, focus);
  }
  userMap(userId) {
    return this.getMap(`user/${userId}`);
  }
  async initialize(option) {
    WhiteboardApplication.instanceCount.set(this.appId, (WhiteboardApplication.instanceCount.get(this.appId) ?? 0) + 1);
    log(`whiteboard ${this.appId} initialize. instance count: ${WhiteboardApplication.instanceCount.get(this.appId) ?? 0}`, {}, 'info');
    this.appDoc.transact(() => {
      this.permissions = new WhiteboardPermissions(this.writableManager, this.userManager, userId => {
        return this.userMap(userId);
      });
      this.permissions.setPermission(WhiteboardPermissionFlag.all);
      // @ts-ignore
      this.emitter['permissions'] = this.permissions;
      // @ts-ignore
      this.emitter['selfUserId'] = this.userId;
      this.option = option;
      if (this.option.stretchToFill) {
        this.getMap('attrs').set('viewportWidth', -1);
        this.getMap('attrs').set('viewportHeight', -1);
      } else {
        if (!this.getMap('attrs').has('viewportWidth')) {
          this.getMap('attrs').set('viewportWidth', option.width);
        }
        if (!this.getMap('attrs').has('viewportHeight')) {
          this.getMap('attrs').set('viewportHeight', option.height);
        }
      }
      this.userMap(this.userId).set(WhiteboardKeys.themeColor, '#009688');
      this.userMap(this.userId).observe(this.handleSyncedWhiteboardStatusChange);
      this.shadowEmitter = new ShadowEmitter(this.userMap(this.userId));
      this.pageModel = new PageModel(this.getMap('attrs'), this.userManager, userId => {
        return this.userMap(userId);
      }, this.getMap('pages'), this.hasPermission);
      this.pageModel.on('pagesChange', this.handleLayersChange);
      this.pageModel.on('switchPage', this.handlePageSwitch);
      if (option.maxScaleRatio && option.maxScaleRatio < 1 && option.maxScaleRatio !== -1) {
        throw new Error(`[@netless/forge-whiteboard] invalid maxScaleRatio ${option.maxScaleRatio}`);
      }
      this.camera = new Camera(new this.paperScope.Size(option.width, option.height), option.maxScaleRatio ?? -1, this.rootElement, this.userManager, this.paperScope, this.getMap('attrs'), this.hasPermission, userId => {
        return this.userMap(userId);
      }, () => {
        return [this.paperScope.project.view.size.width, this.paperScope.project.view.size.height];
      }, () => {
        const rect = this.canvasElement.getBoundingClientRect();
        return [rect.width, rect.height];
      });
      this.camera.on('zoom', matrix => {
        this.paperScope.project.view.matrix = matrix;
        this.shadowScope.project.view.matrix = matrix;
        this.editors.forEach(editor => {
          editor.updateBounds();
        });
        const area = this.paperScope.project.view.size.width * this.paperScope.project.view.size.height;
        const minDistance = Math.ceil(area / 400000);
        Object.keys(this.tools).forEach(key => {
          this.tools[key].tool.minDistance = minDistance;
        });
      });
      this.camera.on('userPageChange', (userId, pageId) => {
        this.emitter.emit('activePageChange', userId, pageId);
      });
      this.camera.on('userViewModeChange', (userId, mode, oldMode) => {
        if (oldMode !== 'main' && mode === 'main') {
          this.emitter.setViewModeToMain(userId);
        }
        this.emitter.emit('userViewModeChange', userId, mode);
      });
      this.rootElement.style.position = 'relative';
      this.rootElement.appendChild(this.canvasElement);
      this.toolbarModel = new ToolbarModel(this.userMap(this.userId), Object.assign({
        tool: 'grab',
        strokeColor: '#009688',
        fillColor: null,
        fontSize: 24,
        fontFamily: 'Courier New',
        strokeWidth: 4,
        dashArray: []
      }, option.defaultToolbarStyle ?? {}));
      this.trashedElementsModel = new TrashedElementsModel(this.userManager, userId => {
        return this.userMap(userId);
      }, this.hasPermission);
      this.selectElementsModel = new SelectElementsModel(this.userManager, userId => {
        return this.userMap(userId);
      }, this.hasPermission);
      this.liveCursor = new LiveCursor(this.canvasElement, this.paperScope, this.userManager, userId => {
        return this.userMap(userId);
      });
      this.tools = {
        pointer: new PointerTool(this.enableToolEvent, this.getCurrentRendererModel, this.shadowEmitter, this.paperScope),
        curve: new CurveTool(this.enableToolEvent, this.getCurrentRendererModel, this.shadowEmitter, this.paperScope),
        rectangle: new RectangleTool(this.enableToolEvent, this.getCurrentRendererModel, this.shadowEmitter, this.paperScope),
        selector: new SelectorTool(this.enableToolEvent, this.getCurrentRendererModel, this.shadowEmitter, this.paperScope, this.selectElementsModel),
        arrow: new LineTool(this.enableToolEvent, this.getCurrentRendererModel, this.shadowEmitter, this.paperScope),
        line: new StraightLineTool(this.enableToolEvent, this.getCurrentRendererModel, this.shadowEmitter, this.paperScope),
        text: new TextTool(this.enableToolEvent, this.getCurrentRendererModel, this.shadowEmitter, this.paperScope, this.rootElement, this.canvasElement, this.toolbarModel, this.camera),
        ellipse: new EllipseTool(this.enableToolEvent, this.getCurrentRendererModel, this.shadowEmitter, this.paperScope),
        triangle: new TriangleTool(this.enableToolEvent, this.getCurrentRendererModel, this.shadowEmitter, this.paperScope),
        eraser: new EraserTool(this.enableToolEvent, this.getCurrentRendererModel, this.shadowEmitter, this.paperScope, this.trashedElementsModel, this.shadowScope),
        laser: new LaserPointerTool(this.enableToolEvent, this.getCurrentRendererModel, this.shadowEmitter, this.paperScope),
        grab: new GrabTool(this.enableToolEvent, this.getCurrentRendererModel, this.shadowEmitter, this.paperScope, this.camera)
      };
      this.toolbarModel.on('update', style => {
        if (this.tools[style.tool]) {
          this.paperScope.tool = this.tools[style.tool].tool;
          if (style.tool === 'pointer') {
            this.rootElement.style.pointerEvents = 'none';
          } else {
            this.rootElement.style.pointerEvents = 'auto';
          }
        } else {
          this.emitter.emit('error', 300001, `${style.tool} not supported`);
        }
        this.emitter.emit('toolbarStyleChange', style);
      });
      if (this.tools[this.toolbarModel.currentTool]) {
        this.paperScope.tool = this.tools[this.toolbarModel.currentTool].tool;
      } else {
        this.paperScope.tool = this.tools['curve'].tool;
        log(`${this.toolbarModel.currentTool} not supported, backup to curve`);
      }
      this.selectElementsModel.on('elementsChange', this.handleElementsSelect);
      this.trashedElementsModel.on('elementsChange', this.handleElementsTrash);
      this.trashedElementsModel.on('removeElementForSelf', this.handleRemoveTrashedElementForSelf);
      this.canvasElement.style.background = '#FFEB3B';
      this.canvasElement.style.position = 'absolute';
      this.canvasElement.style.top = '50%';
      this.canvasElement.style.left = '50%';
      this.canvasElement.style.transform = 'translate(-50%, -50%)';
      this.canvasElement.setAttribute('id', `${this.appId}-main`);
      this.paperScope.setup(this.canvasElement);
      this.paperScope.settings.insertItems = false;
      this.shadowCanvasElement.style.position = 'absolute';
      this.shadowCanvasElement.style.top = '50%';
      this.shadowCanvasElement.style.left = '50%';
      this.shadowCanvasElement.style.transform = 'translate(-50%, -50%)';
      this.shadowCanvasElement.style.pointerEvents = 'none';
      this.shadowCanvasElement.setAttribute('id', `${this.appId}-shadow`);
      this.rootElement.appendChild(this.shadowCanvasElement);
      this.shadowScope.setup(this.shadowCanvasElement);
      this.shadowScope.settings.insertItems = false;
      this.snapshotCanvasElement.setAttribute('id', `${this.appId}-snapshot`);
      this.snapshotScope.setup(this.snapshotCanvasElement);
      this.snapshotScope.settings.insertItems = false;
      this.resizeObserver = new ResizeObserver(() => {
        if (this.internalResizeObserver) {
          const rootBounds = this.rootElement.getBoundingClientRect();
          this.adjustByOutFrame(rootBounds.width, rootBounds.height);
        }
      });
      this.resizeObserver.observe(this.rootElement);
      if (!this.getMap('attrs').has(WhiteboardKeys.currentPage)) {
        this.getMap('attrs').set(WhiteboardKeys.currentPage, '_i_');
      }
      this.emitter.indexedNavigation = new IndexedNavigation(this.pageModel, this.userMap(this.userId), this.getMap('indexedNavigation'), this.hasPermission);
      this.emitter.indexedNavigation.initIndexed().then(inited => {
        if (!inited) {
          // 如果 inited 为 false, 代表 WhiteboardApplication 初始化时候就有数据了
          this.handleLayersChange(this.pageModel.pageList());
          const currentPage = this.pageModel.getCurrentPage(this.userId);
          if (currentPage) {
            this.userMap(this.userId).set(WhiteboardKeys.currentPage, currentPage);
            this.handlePageSwitch(currentPage);
          } else {
            this.userMap(this.userId).set(WhiteboardKeys.currentPage, '_i_');
            this.handlePageSwitch('_i_');
          }
        } else {
          this.userMap(this.userId).set(WhiteboardKeys.currentPage, '_i_');
        }
      });
      this.shadowEmitter.on('translateOut', (ids, container) => {
        if (this.delayTranslateOut > 0) {
          setTimeout(() => {
            this.handleElementTranslateOut(ids, container);
          }, this.delayTranslateOut);
        } else {
          this.handleElementTranslateOut(ids, container);
        }
      });
      this.shadowEmitter.on('translateIn', (ids, container) => {
        let parent = null;
        if (container === 'layer') {
          parent = this.shadowScope.project.activeLayer;
        } else {
          parent = this.shadowScope.project.activeLayer.children.find(child => child.data.uuid === container) ?? null;
        }
        if (parent) {
          ids.forEach(id => {
            const target = this.paperScope.project.activeLayer.children.find(child => child.data.uuid === id);
            if (target) {
              target.remove();
              this.insertElementToParent(target, parent);
            }
          });
        }
      });
      this.shadowEmitter.on('grabDown', () => {
        this.emitter.emit('grabDown');
      });
      this.shadowEmitter.on('grabUp', () => {
        this.emitter.emit('grabUp');
      });
      this.clearElements();
      if (this.option.stretchToFill) {
        window.addEventListener('resize', () => {
          const bounds = this.rootElement.getBoundingClientRect();
          this.updateOptionSize(bounds.width, bounds.height);
          this.adjustByOutFrame(bounds.width, bounds.height);
        });
      }
      this.rootElement.appendChild(this.liveCursor.container);
      this.getMap('attrs').observe(this.handleViewportUpdate);
      this.writableManager().on('writableChanged', this.handleWritableChanged);
    }, 'whiteboard-initialize');
  }
  clearElements() {
    const userIds = this.userManager.userIdList();
    const isContinue = userIds.every(userId => {
      return this.userMap(userId).get('shadowActive') !== true;
    });
    if (isContinue) {
      const pageId = this.pageModel.getCurrentPage(this.userId);
      if (pageId) {
        const page = this.layers.get(pageId);
        if (page) {
          page.clearElement();
        }
      }
    }
    setTimeout(() => this.clearElements(), 10000);
  }
  findElementIndex(target, parent) {
    const children = parent.children;
    let left = 0;
    let right = children.length - 1;
    let result = children.length;
    while (left <= right) {
      const mid = Math.floor((left + right) / 2);
      if (children[mid].data.index > target) {
        result = mid;
        right = mid - 1;
      } else {
        left = mid + 1;
      }
    }
    return result;
  }
  insertElementToParent(element, parent) {
    const children = parent.children;
    const at = this.findElementIndex(element.data.index, parent);
    for (let i = at; i >= 0; i--) {
      const child = children[i - 1];
      const childRole = child?.data.role === 'background' ? 0 : 1;
      const elementRole = element.data.role === 'background' ? 0 : 1;
      if (!child || childRole < elementRole || childRole === elementRole && child.data.index < element.data.index || childRole === elementRole && child.data.index === element.data.index && child.data.uuid < element.data.uuid) {
        parent.insertChild(i, element);
        break;
      }
    }
  }
  ensureScale(outputWidth, outputHeight, originSize) {
    return Math.min(outputWidth / originSize.width, outputHeight / originSize.height);
  }
  rasterize(outputMaxWidth, outputMaxHeight, scale, area, page) {
    let pageId;
    if (typeof page === 'string') {
      pageId = page;
    } else if (typeof page === 'number') {
      pageId = this.emitter.indexedNavigation.getPageId(page);
    } else {
      pageId = this.pageModel.getCurrentPage(this.userId);
    }
    const renderableModel = this.layers.get(pageId || '');
    if (!renderableModel) {
      return Promise.resolve(null);
    }
    this.snapshotScope.project.activeLayer.removeChildren();
    for (const key of Array.from(renderableModel.elements.keys())) {
      let elementModel = null;
      if (renderableModel.elementModels.has(key)) {
        elementModel = renderableModel.elementModels.get(key) ?? null;
      } else {
        const elementMap = renderableModel.elements.get(key);
        if (elementMap) {
          elementModel = renderableModel.convertToModel(elementMap);
        }
      }
      if (elementModel) {
        if (!elementModel.item) {
          elementModel.createPaperElement();
        }
        if (elementModel.item) {
          const paperItem = elementModel.item.clone({
            insert: false
          });
          this.snapshotScope.project.activeLayer.addChild(paperItem);
        }
      }
    }
    if (area === 'bounds') {
      const bounds = this.snapshotScope.project.activeLayer.bounds;
      const scale = this.ensureScale(outputMaxWidth, outputMaxHeight, bounds.size);
      const matrix = new this.paperScope.Matrix();
      matrix.scale(scale);
      matrix.translate({
        x: -bounds.x,
        y: -bounds.y
      });
      this.snapshotScope.view.viewSize = bounds.size.multiply(scale);
      this.snapshotScope.view.matrix = matrix;
    } else if (area === 'maxScale' && this.option.maxScaleRatio && this.option.maxScaleRatio !== -1) {
      const width = this.viewportWidth * this.option.maxScaleRatio;
      const height = this.viewportHeight * this.option.maxScaleRatio;
      const offsetX = this.viewportWidth * (this.option.maxScaleRatio - 1) / 2;
      const offsetY = this.viewportHeight * (this.option.maxScaleRatio - 1) / 2;
      const matrix = new this.paperScope.Matrix();
      matrix.scale(scale);
      matrix.translate({
        x: offsetX,
        y: offsetY
      });
      this.snapshotScope.view.viewSize = new this.snapshotScope.Size(width, height).multiply(scale);
      this.snapshotScope.view.matrix = matrix;
    } else {
      this.snapshotScope.view.matrix = this.paperScope.project.view.matrix.clone();
      this.snapshotScope.view.matrix.scale(scale, this.paperScope.project.view.bounds.topLeft);
      this.snapshotScope.view.viewSize = this.paperScope.project.view.viewSize.clone().multiply(scale);
    }
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        try {
          const res = this.snapshotScope.view.element.toDataURL('image/png');
          resolve(res);
        } catch (error) {
          reject(error);
        }
      }, 32);
    });
  }
  disableViewModel() {
    this.disableViewModelUpdate = true;
  }
  linkToWhiteboard(targetId) {
    this.linkWhiteboardId = targetId;
    window.__forge_gl_wb_status__.get(targetId).then(currentStatus => {
      if (currentStatus) {
        this.toolbarModel.currentTool = currentStatus.currentTool;
        this.toolbarModel.strokeColor = currentStatus.strokeColor;
        this.toolbarModel.fillColor = currentStatus.fillColor;
        this.toolbarModel.fontSize = currentStatus.fontSize;
        this.toolbarModel.fontFamily = currentStatus.fontFamily;
        this.toolbarModel.strokeWidth = currentStatus.strokeWidth;
        this.toolbarModel.dashArray = currentStatus.dashArray;
        this.permissions.removePermission(WhiteboardPermissionFlag.all);
        this.permissions.addPermission(currentStatus.permission);
      }
    });
  }
  unlink() {
    this.linkWhiteboardId = null;
  }
  setViewSize(width, height) {
    this.paperScope.project.view.viewSize = new this.paperScope.Size(width, height);
    this.shadowScope.project.view.viewSize = new this.paperScope.Size(width, height);
  }
  getViewSize() {
    const size = this.paperScope.view.viewSize;
    return [size.width, size.height];
  }
  getInherentScale() {
    return this.camera.getInherentScale();
  }
  updateInternalResizeObserverStatus(value) {
    this.internalResizeObserver = value;
  }
  updateOptionSize(width, height) {
    this.getMap('attrs').set('viewportWidth', width);
    this.getMap('attrs').set('viewportHeight', height);
    this.camera.updateInitSize(new paper.Size(width, height));
  }
  async dispose(removeSubDoc) {
    WhiteboardApplication.instanceCount.set(this.appId, (WhiteboardApplication.instanceCount.get(this.appId) ?? 0) - 1);
    if (removeSubDoc) {
      this.deleteSubDoc(this.appId);
    }
    this.selectElementsModel.dispose();
    this.trashedElementsModel.dispose();
    this.paperScope.view.remove();
    this.shadowScope.view.remove();
    this.rootElement.removeChild(this.canvasElement);
    this.rootElement.removeChild(this.shadowCanvasElement);
    this.paperScope.project.clear();
    this.shadowScope.project.clear();
    this.snapshotScope.project.clear();
    this.resizeObserver.disconnect();
    this.unlink();
    for (const entry of this.undoManagers.entries()) {
      entry[1].off('stack-item-added', this.handleStackItemAdded);
      entry[1].off('stack-item-popped', this.handleStackItemPopped);
    }
    for (const entry of this.layers.entries()) {
      entry[1].dispose(removeSubDoc);
      entry[1].removeAllListeners();
    }
    this.camera.dispose();
    this.pageModel.dispose();
    this.pageModel.removeAllListeners();
    for (const entry of this.editors.entries()) {
      entry[1].dispose();
    }
    this.toolbarModel.dispose();
    this.emitter.indexedNavigation.dispose();
    this.permissions.dispose();
    removeObserver(this.userMap(this.userId), this.handleSyncedWhiteboardStatusChange);
    log(`whiteboard ${this.appId} disposed. instance count: ${WhiteboardApplication.instanceCount.get(this.appId) ?? 0}`, {}, 'info');
  }
}
_defineProperty(WhiteboardApplication, "instanceCount", new Map());
_defineProperty(WhiteboardApplication, "applicationName", WHITEBOARD_APP_NAME);